/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { upperFirst } from 'lodash-es';
import { Subject, forkJoin, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';

import { Company, CompanyBilling } from '@site-mate/dashpivot-shared-library';
import {
  AccountTypes,
  IAccount,
  PaymentCurrencyTypes,
  PaymentMethods,
  PaymentTermContexts,
} from '@site-mate/sitemate-global-shared';

import { GlobalApiService } from 'app/global-api/global-api.service';
import { ICompanyHomeSettings } from 'app/home/company-home-settings.model';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { LastVisitedHomeService } from 'app/shared/service/last-visited-home.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import {
  IGlobalBillingSettingsForm,
  WorkspaceBillingService,
} from 'app/shared/service/workspace-billing.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-workspace-sms-global-billing-settings',
  templateUrl: './workspace-sms-global-billing-settings.component.html',
  styleUrls: ['./workspace-sms-global-billing-settings.component.scss'],
})
export class WorkspaceSmsGlobalBillingSettingsComponent implements OnInit, OnDestroy {
  isSuperAdmin: boolean;
  isLoading = true;
  isSaving = false;
  company: Company;
  account: IAccount;
  groupBillingTooltip: string;
  legacyRestrictionsTooltip: string;
  didAnyTextInputChange = false;

  formModel: IGlobalBillingSettingsForm = {
    billingCompany: '',
    billingAddress: '',
    billingContact: '',
    billingDate: null,
    initialSubscriptionDate: null,
    paymentTermDay: null,
    paymentTermContext: null,
    paymentMethod: null,
    paymentCurrency: null,
    accountType: null,
    automatedBilling: false,
  };

  @ViewChild('form') ngBillingForm: NgForm;

  private unsubscribe = new Subject<void>();

  readonly paymentTermDaySelectorValues = Array.from(Array(61).keys());
  readonly paymentTermContextSelectorValues = this.getSelectorOptions(PaymentTermContexts);
  readonly paymentMethodSelectorValues = this.getSelectorOptions(PaymentMethods);
  readonly paymentCurrencySelectorValues = this.getSelectorOptions(PaymentCurrencyTypes);
  readonly accountTypeSelectorValues = this.getSelectorOptions(AccountTypes);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
    private readonly globalApiService: GlobalApiService,
    private readonly workspaceBillingService: WorkspaceBillingService,
    private readonly lastVisitedHomeService: LastVisitedHomeService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.setupModel();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  automatedBillingToggleClicked() {
    this.formModel.automatedBilling = !this.formModel.automatedBilling;
  }

  canDisplayDatePicker(event: Event) {
    if (!this.isSuperAdmin) {
      event.preventDefault();
    }

    return this.isSuperAdmin;
  }

  setupModel(): void {
    this.isSuperAdmin = this.userService.isAdmin();
    this.flagTextInputChange(false);
    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => this.companyService.getCompany(params.companyId)),
        switchMap((company) =>
          this.globalApiService.getAccount(company.accountId).pipe(map((account) => ({ company, account }))),
        ),
        takeUntil(this.unsubscribe),
      )
      .subscribe({
        next: ({ company, account }) => {
          this.company = company;
          this.account = account;
          this.formModel = this.workspaceBillingService.buildGlobalBillingSettingsForm(account);
          this.isLoading = false;
        },
        error: (error) => {
          this.errorHandler.handle(error);
          this.lastVisitedHomeService
            .goToFirstPossibleTeam(this.router, true)
            .pipe(takeUntil(this.unsubscribe));
        },
      });
  }

  get changedFormFields(): string[] {
    if (!this.ngBillingForm?.controls) return [];
    const allFormFields = Object.entries(this.ngBillingForm.controls);

    return allFormFields.filter(([, value]) => value.dirty).map(([key]) => key);
  }

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.canSaveSettings) {
      return;
    }

    this.isSaving = true;
    await this.updateBilling();
  }

  private resetForm(): void {
    this.ngBillingForm.resetForm(this.ngBillingForm.value);
  }

  private mapFormToLegacyBillingSettings(form: IGlobalBillingSettingsForm) {
    const legacyBillingSettings: ICompanyHomeSettings = {
      ...this.companyService.getCompanyModel(this.company),

      subscriptionInitialDate: form.initialSubscriptionDate,
      paymentTermDay: form.paymentTermDay,
      paymentTermContext: form.paymentTermContext,
      paymentMethod: form.paymentMethod,
      paymentCurrency: form.paymentCurrency,
    };

    return legacyBillingSettings;
  }

  private updateLegacyBillingSettings() {
    const updatedLegacyBillingSettings: ICompanyHomeSettings = this.mapFormToLegacyBillingSettings(
      this.formModel,
    );
    const legacyCompanyBilling: CompanyBilling = this.workspaceBillingService.buildLegacyCompanyBilling(
      updatedLegacyBillingSettings,
    );

    return this.companyService.updateCompanyBilling(this.company.id, legacyCompanyBilling);
  }

  private updateGlobalBillingSettings() {
    const billingDate = this.formModel.billingDate
      ? this.workspaceBillingService.convertToISODate(this.formModel.billingDate)
      : null;
    const initialSubscriptionDate = this.formModel.initialSubscriptionDate
      ? this.workspaceBillingService.convertToISODate(this.formModel.initialSubscriptionDate)
      : null;

    return this.globalApiService
      .updateAccount(this.account._id, {
        accountType: this.formModel.accountType,
        automatedBilling: this.formModel.automatedBilling,
      })
      .pipe(
        switchMap(() => {
          return this.globalApiService.updateAccountBillingSettings(this.account._id, {
            billingCompanyName: this.formModel.billingCompany,
            billingAddress: this.formModel.billingAddress,
            billingContact: this.formModel.billingContact,
            billingDate,
            initialSubscriptionDate,
            paymentTermDay: this.formModel.paymentTermDay,
            paymentTermContext: this.formModel.paymentTermContext,
            paymentMethod: this.formModel.paymentMethod,
            currency: this.formModel.paymentCurrency,
          });
        }),
      );
  }

  private async updateBilling(): Promise<void> {
    try {
      await lastValueFrom(
        forkJoin([
          // Write to DP Database
          this.updateLegacyBillingSettings(),
          // Write to Global Database
          this.updateGlobalBillingSettings(),
        ]),
      );

      this.setupModel();
      this.toastrService.successByKey('sitemateStartSettingsUpdateSuccess');
      this.resetForm();
    } catch (error) {
      // @ts-expect-error implicitly has an 'any' type.
      error.toastKey = 'sitemateStartSettingsUpdateFailed';
      this.errorHandler.handle(error);
    } finally {
      this.isSaving = false;
    }
  }

  get canSaveSettings() {
    const hasFormChanged =
      this.ngBillingForm?.form.dirty ||
      this.changedFormFields.length ||
      this.hasInputTogglesChanged ||
      this.didAnyTextInputChange;

    return this.isSuperAdmin && hasFormChanged && !this.isSaving;
  }

  get hasInputTogglesChanged(): boolean {
    if (!this.account?.billing) {
      return false;
    }

    const hasAutomatedBillingChanged = this.formModel.automatedBilling !== this.account.automatedBilling;

    return hasAutomatedBillingChanged;
  }

  /**
   * TODO: Once custom text input component is configured to be captured by ngForm, this method should be removed.
   * The current custom text input component doesn't automatically get registered in the ngForm's controls
   * which means we can't track if it's dirty. This method is a workaround to track changes on the custom text inputs
   * similar to how the existing hasInputTogglesChanged is implemented.
   */
  private flagTextInputChange(isChanged = true) {
    this.didAnyTextInputChange = isChanged;
  }

  private getSelectorOptions(options: any) {
    return Object.values(options).map((option) => {
      return { label: upperFirst(option), value: option };
    });
  }
}
